"use client";

import posthog from "posthog-js";
import { useRef, type VideoHTMLAttributes, type AudioHTMLAttributes } from "react";

/**
 * Native <video>/<audio> wrappers that report engagement to PostHog.
 *
 * Why: pageviews are captured site-wide by app/PostHogProvider.tsx, but the
 * native player controls live inside the browser's own widget, which
 * autocapture cannot see. "Pressed play" and "how far they got" are the
 * signals that matter for a note, so we listen to the media events directly.
 *
 * Events (all carry note_slug + note_title + media: "video" | "audio"):
 *   note_media_play      first play only (one per page load)
 *   note_media_progress  once each at 25 / 50 / 75 percent — { percent }
 *   note_media_complete  ended, or progress reached 100
 *
 * posthog.capture is a no-op when the provider did not init (non-kreayt.ai
 * hosts), so previews and dev never pollute the numbers.
 */
type Meta = { slug: string; title: string; media: "video" | "audio" };

const MILESTONES = [25, 50, 75] as const;

/** Pass null to get inert handlers (the homepage row is not a note). */
export function useMediaTracking(meta: Meta | null) {
  const started = useRef(false);
  const hit = useRef(new Set<number>());
  const completed = useRef(false);

  const capture = (event: string, extra: Record<string, unknown> = {}) => {
    if (!meta || !posthog.__loaded) return;
    posthog.capture(event, { note_slug: meta.slug, note_title: meta.title, media: meta.media, ...extra });
  };

  return {
    onPlay: () => {
      if (started.current) return;
      started.current = true;
      capture("note_media_play");
    },
    onTimeUpdate: (e: React.SyntheticEvent<HTMLMediaElement>) => {
      const el = e.currentTarget;
      if (!el.duration || !isFinite(el.duration)) return;
      const pct = (el.currentTime / el.duration) * 100;
      for (const m of MILESTONES) {
        if (pct >= m && !hit.current.has(m)) {
          hit.current.add(m);
          capture("note_media_progress", { percent: m });
        }
      }
    },
    onEnded: () => {
      if (completed.current) return;
      completed.current = true;
      capture("note_media_complete");
    },
  };
}

export function TrackedVideo({
  slug,
  title,
  ...rest
}: { slug: string; title: string } & VideoHTMLAttributes<HTMLVideoElement>) {
  const handlers = useMediaTracking({ slug, title, media: "video" });
  return <video {...rest} {...handlers} />;
}

export function TrackedAudio({
  slug,
  title,
  ...rest
}: { slug: string; title: string } & AudioHTMLAttributes<HTMLAudioElement>) {
  const handlers = useMediaTracking({ slug, title, media: "audio" });
  return <audio {...rest} {...handlers} />;
}
